import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import ReceivingDetailModal from '../components/ReceivingDetailModal';
import { fetchPurchaseReturns } from '../api/returns';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { PurchaseReturnRow, User } from '../api/types';
import DrillCell from '../components/DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

export default function PurchasingReturns() {
  const user = useOutletContext<User>();
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [supId, setSupId] = useState('');
  // Shared header store (lib/storeFilter); PurchasingLayout renders the picker.
  const [sid] = useStoreFilter(user);
  const [rows, setRows] = useState<PurchaseReturnRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<{ sid: number; onum: number; receiveNo: number } | null>(null);
  const pg = usePagination(rows);

  const load = () => {
    setErr(null);
    setRows(null);
    fetchPurchaseReturns({ from, to, sid, sup_id: supId || undefined })
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [from, to, supId, sid]);

  const totalExt = rows ? rows.reduce((acc, r) => acc + r.ext_mxn, 0) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.returns.purchasingTitle}</Title>
          <Text className="text-tremor-content text-sm">{t.returns.purchasingSubtitle}</Text>
        </div>
      </div>

      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">Proveedor (ID)</div>
            <TextInput value={supId} onValueChange={setSupId} placeholder="ABC" className="w-40" />
          </label>
          <div className="ml-auto text-sm text-tremor-content">
            {rows?.length ?? 0} líneas · Importe {fmtMxn.format(totalExt)}
          </div>
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && rows === null && <TableSkeleton rows={6} cols={9} />}
      {!err && rows !== null && rows.length === 0 && <EmptyState message={t.returns.empty} />}
      {!err && rows !== null && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.returns.cols.date}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.onum}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.recvNo}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.sup}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.store}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.part}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.returns.cols.qty}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.returns.cols.cost}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.returns.cols.ext}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r, i) => (
                <TableRow
                  key={`${r.sid}-${r.onum}-${r.receive_no}-${r.line}-${r.part}-${i}`}
                  className="cursor-pointer hover:bg-tremor-background-muted"
                  onClick={() => setOpen({ sid: r.sid, onum: r.onum, receiveNo: r.receive_no })}
                >
                  <TableCell>{r.xfr_stk}</TableCell>
                  <TableCell>
                    <span onClick={(e) => e.stopPropagation()}>
                      <DrillCell target={{ kind: 'order', sid: r.sid, onum: r.onum }}>
                        {r.onum}
                      </DrillCell>
                    </span>
                  </TableCell>
                  <TableCell>{r.receive_no}</TableCell>
                  <TableCell>{r.sup_id} · {r.sup_name}</TableCell>
                  <TableCell>{r.sid}</TableCell>
                  <TableCell>{r.line} {r.part}<div className="text-xs text-tremor-content">{r.pdesc}</div></TableCell>
                  <TableCell className="text-right">{fmtQty.format(r.qty_returned)}</TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.pcost_mxn)}</TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.ext_mxn)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination {...pg} className="px-4" />
        </Card>
      )}

      {open && (
        <ReceivingDetailModal
          sid={open.sid}
          onum={open.onum}
          receiveNo={open.receiveNo}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
